import React, { useEffect, useRef, useState, useCallback } from 'react';
import 'fabric';
const fabric = window.fabric;
import { Pencil, Circle, ArrowRight, Type, Eraser, Save, X, ZoomIn, ZoomOut, MousePointer, Palette, Square, ChevronDown, Trash, Minus } from 'lucide-react';
import { createPortal } from 'react-dom';

const ColorPicker = ({ show, onClose, currentColor, onColorChange, buttonRef }) => {
  if (!show) return null;

  const buttonRect = buttonRef.current?.getBoundingClientRect();
  const top = buttonRect ? buttonRect.bottom + 8 : 0;
  const left = buttonRect ? buttonRect.left : 0;

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999]"
      onClick={onClose}
    >
      <div 
        className="absolute p-3 bg-white rounded-lg shadow-xl border border-slate-200"
        style={{ top, left }}
        onClick={e => e.stopPropagation()}
      >
        <div className="mb-2 text-sm font-medium text-slate-700">Select Color</div>
        <div className="grid grid-cols-6 gap-2">
          {[
            '#ef4444', '#f97316', '#f59e0b', '#84cc16', 
            '#22c55e', '#14b8a6', '#0ea5e9', '#3b82f6', 
            '#6366f1', '#8b5cf6', '#d946ef', '#000000'
          ].map(color => (
            <button
              key={color}
              onClick={() => onColorChange(color)}
              className={`w-8 h-8 rounded-full border-2 hover:scale-110 transition-transform ${
                color === currentColor ? 'border-blue-500 shadow-sm' : 'border-slate-200'
              }`}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};

const ShapesMenu = ({ show, onClose, onSelect, buttonRef }) => {
  if (!show) return null;

  const buttonRect = buttonRef.current?.getBoundingClientRect();
  const top = buttonRect ? buttonRect.bottom + 8 : 0;
  const left = buttonRect ? buttonRect.left : 0;

  const shapes = [
    { icon: Square, name: 'rectangle', label: 'Rectangle/Square' },
    { icon: Circle, name: 'circle', label: 'Circle' },
    { icon: ArrowRight, name: 'arrow', label: 'Arrow' },
    { icon: Minus, name: 'line', label: 'Line' }
  ];

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999]"
      onClick={onClose}
    >
      <div 
        className="absolute p-2 bg-white rounded-lg shadow-xl border border-slate-200"
        style={{ top, left }}
        onClick={e => e.stopPropagation()}
      >
        {shapes.map(({ icon: Icon, name, label }) => (
          <button
            key={name}
            onClick={() => {
              onSelect(name);
              onClose();
            }}
            className="flex items-center gap-2 w-full px-3 py-2 hover:bg-slate-50 rounded text-left"
          >
            <Icon className="w-4 h-4" />
            <span>{label}</span>
          </button>
        ))}
      </div>
    </div>,
    document.body
  );
};

const ImageEditor = ({ imageUrl, onSave, onClose }) => {
  const canvasRef = useRef(null);
  const fabricRef = useRef(null);
  const colorButtonRef = useRef(null);
  const [activeButton, setActiveButton] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [zoom, setZoom] = useState(1);
  const [currentColor, setCurrentColor] = useState('#ef4444');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showShapesMenu, setShowShapesMenu] = useState(false);
  const shapesButtonRef = useRef(null);
  const [isDrawingLine, setIsDrawingLine] = useState(false);
  const [lineStartPoint, setLineStartPoint] = useState(null);
  const tempLineRef = useRef(null);

  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);

  useEffect(() => {
    historyRef.current = history;
    historyIndexRef.current = historyIndex;
  }, [history, historyIndex]);

  const saveState = useCallback(() => {
    if (!fabricRef.current) return;
    
    try {
      const json = fabricRef.current.toJSON(['selectable', 'hasControls']);
      const jsonStr = JSON.stringify(json);
      
      setHistory(prev => {
        const newHistory = prev.slice(0, historyIndexRef.current + 1);
        return [...newHistory, jsonStr];
      });
      
      setHistoryIndex(prev => prev + 1);
    } catch (error) {
      console.error('Error saving state:', error);
    }
  }, []);

  const debouncedSaveState = useCallback(() => {
    if (fabricRef.current?.isDrawingMode) {
      return;
    }
    if (window.saveStateTimeout) {
      clearTimeout(window.saveStateTimeout);
    }
    window.saveStateTimeout = setTimeout(() => {
      saveState();
    }, 100);
  }, [saveState]);

  const loadState = useCallback((index) => {
    if (!fabricRef.current || !historyRef.current[index]) return;
    
    try {
      fabricRef.current.clear();
      const state = JSON.parse(historyRef.current[index]);
      fabricRef.current.loadFromJSON(state, () => {
        fabricRef.current.getObjects().forEach(obj => {
          if (obj.type === 'i-text') {
            obj.set({
              editable: true,
              selectable: true
            });
          }
          if (obj.type === 'image') {
            obj.set({
              selectable: false,
              hasControls: false,
              lockMovementX: true,
              lockMovementY: true,
              lockRotation: true,
              lockScalingX: true,
              lockScalingY: true
            });
          }
        });
        fabricRef.current.renderAll();
      });
    } catch (error) {
      console.error('Error loading state:', error);
    }
  }, []);

  const handleUndo = useCallback(() => {
    if (historyIndexRef.current > 0) {
      const newIndex = historyIndexRef.current - 1;
      setHistoryIndex(newIndex);
      loadState(newIndex);
    }
  }, [loadState]);

  const handleRedo = useCallback(() => {
    if (historyIndexRef.current < historyRef.current.length - 1) {
      const newIndex = historyIndexRef.current + 1;
      setHistoryIndex(newIndex);
      loadState(newIndex);
    }
  }, [loadState]);

  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    const toolbarHeight = 60;
    const headerHeight = 60;
    const footerHeight = 80;
    const availableHeight = window.innerHeight - toolbarHeight - headerHeight - footerHeight;
    
    const canvasWidth = isMobile ? window.innerWidth - 32 : 800;
    const canvasHeight = isMobile ? availableHeight - 32 : 600;

    fabricRef.current = new fabric.Canvas(canvasRef.current, {
      width: canvasWidth,
      height: canvasHeight,
      backgroundColor: '#f8fafc',
      preserveObjectStacking: true,
      isDrawingMode: false,
      selection: true,
      enableRetinaScaling: true,
      stopContextMenu: true,
      perPixelTargetFind: false,
      targetFindTolerance: 4,
      allowTouchScrolling: false,
      interactive: true
    });

    fabric.Image.fromURL(imageUrl, (img) => {
      img.selectable = false;
      img.evented = false;
      img.hoverCursor = 'default';
      const scale = Math.min(
        (fabricRef.current.width - 20) / img.width,
        (fabricRef.current.height - 20) / img.height
      );
      img.scale(scale);
      
      img.set({
        id: 'background',
        left: (fabricRef.current.width - img.width * scale) / 2,
        top: (fabricRef.current.height - img.height * scale) / 2,
        lockMovementX: true,
        lockMovementY: true,
        lockRotation: true,
        lockScalingX: true,
        lockScalingY: true,
        hasControls: false,
        hasBorders: false,
        selectable: false,
        evented: false,
        excludeFromExport: false
      });

      fabricRef.current.add(img);
      fabricRef.current.sendToBack(img);

      const overlay = new fabric.Rect({
        left: 0,
        top: 0,
        width: canvasWidth,
        height: canvasHeight,
        fill: 'transparent',
        selectable: false,
        evented: true,
        excludeFromExport: true
      });
      fabricRef.current.add(overlay);
      fabricRef.current.sendToBack(overlay);
      fabricRef.current.sendToBack(img);

      fabricRef.current.renderAll();
      
      saveState();
    });

    fabricRef.current.freeDrawingBrush = new fabric.PencilBrush(fabricRef.current);
    fabricRef.current.freeDrawingBrush.width = isMobile ? 3 : 2;
    fabricRef.current.freeDrawingBrush.color = currentColor;

    const eraserBrush = new fabric.PencilBrush(fabricRef.current);
    eraserBrush.width = isMobile ? 15 : 10;
    eraserBrush.color = '#f8fafc';
    fabricRef.current.eraserBrush = eraserBrush;

    fabricRef.current.on('selection:created', function(e) {
      if (e.target && e.target.type === 'image') {
        fabricRef.current.discardActiveObject();
      }
    });

    fabricRef.current.on('touch:gesture', function(opt) {
      if (opt.e) {
        opt.e.preventDefault();
      }
    });

    fabricRef.current.on('path:created', () => {
      requestAnimationFrame(saveState);
    });

    fabricRef.current.on('object:modified', debouncedSaveState);

    fabricRef.current.on('object:added', (e) => {
      if (e.target.type !== 'image' && e.target.type !== 'path') {
        debouncedSaveState();
      }
    });

    fabricRef.current.on('object:removed', debouncedSaveState);

    return () => {
      if (window.saveStateTimeout) {
        clearTimeout(window.saveStateTimeout);
      }
      if (fabricRef.current) {
        fabricRef.current.dispose();
      }
    };
  }, [imageUrl, saveState, currentColor, debouncedSaveState]);

  useEffect(() => {
    if (fabricRef.current) {
      if (activeButton === 'move') {
        fabricRef.current.defaultCursor = 'move';
      } else if (activeButton === 'draw') {
        fabricRef.current.defaultCursor = 'crosshair';
      } else if (activeButton === 'eraser') {
        fabricRef.current.defaultCursor = 'crosshair';
      } else {
        fabricRef.current.defaultCursor = 'default';
      }
    }
  }, [activeButton]);

  useEffect(() => {
    if (!fabricRef.current) return;

    const handleMouseDown = function(opt) {
      if (activeButton === 'move' && !opt.target) {
        const evt = opt.e;
        this.isDragging = true;
        this.selection = false;
        this.lastPosX = evt.clientX;
        this.lastPosY = evt.clientY;
      }
    };

    const handleMouseMove = function(opt) {
      if (this.isDragging) {
        const evt = opt.e;
        const vpt = this.viewportTransform;
        vpt[4] += evt.clientX - this.lastPosX;
        vpt[5] += evt.clientY - this.lastPosY;
        this.requestRenderAll();
        this.lastPosX = evt.clientX;
        this.lastPosY = evt.clientY;
      }
    };

    const handleMouseUp = function() {
      this.isDragging = false;
      this.selection = true;
    };

    fabricRef.current.on('mouse:down', handleMouseDown);
    fabricRef.current.on('mouse:move', handleMouseMove);
    fabricRef.current.on('mouse:up', handleMouseUp);

    return () => {
      if (fabricRef.current) {
        fabricRef.current.off('mouse:down', handleMouseDown);
        fabricRef.current.off('mouse:move', handleMouseMove);
        fabricRef.current.off('mouse:up', handleMouseUp);
      }
    };
  }, [activeButton]);

  useEffect(() => {
    if (!fabricRef.current) return;

    fabricRef.current.on('mouse:dblclick', function(options) {
      if (options.target && options.target.type === 'i-text') {
        options.target.enterEditing();
        options.target.selectAll();
      }
    });

    fabricRef.current.on('mouse:wheel', function(opt) {
      const delta = opt.e.deltaY;
      let newZoom = fabricRef.current.getZoom() - delta / 400;
      newZoom = Math.min(Math.max(0.5, newZoom), 5);
      
      const point = {
        x: opt.e.offsetX,
        y: opt.e.offsetY
      };
      
      fabricRef.current.zoomToPoint(point, newZoom);
      setZoom(newZoom);
      opt.e.preventDefault();
      opt.e.stopPropagation();
    });

    const handleResize = () => {
      const isMobile = window.innerWidth <= 768;
      const toolbarHeight = 60;
      const headerHeight = 60;
      const footerHeight = 80;
      const availableHeight = window.innerHeight - toolbarHeight - headerHeight - footerHeight;
      
      const newWidth = isMobile ? window.innerWidth - 32 : 800;
      const newHeight = isMobile ? availableHeight - 32 : 600;
      
      fabricRef.current.setDimensions({
        width: newWidth,
        height: newHeight
      });
      fabricRef.current.renderAll();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      fabricRef.current.off('mouse:dblclick');
      fabricRef.current.off('mouse:wheel');
    };
  }, []);

  useEffect(() => {
    if (!fabricRef.current) return;

    const deleteIcon = document.createElement('canvas');
    const deleteIconCtx = deleteIcon.getContext('2d');
    deleteIcon.width = 24;
    deleteIcon.height = 24;

    deleteIconCtx.beginPath();
    deleteIconCtx.arc(12, 12, 10, 0, 2 * Math.PI);
    deleteIconCtx.fillStyle = '#ef4444';
    deleteIconCtx.fill();
    
    deleteIconCtx.beginPath();
    deleteIconCtx.strokeStyle = 'white';
    deleteIconCtx.lineWidth = 2;
    deleteIconCtx.moveTo(8, 8);
    deleteIconCtx.lineTo(16, 16);
    deleteIconCtx.moveTo(16, 8);
    deleteIconCtx.lineTo(8, 16);
    deleteIconCtx.stroke();

    fabric.Object.prototype.controls.deleteControl = new fabric.Control({
      x: 0.5,
      y: -0.5,
      offsetY: -16,
      offsetX: 16,
      cursorStyle: 'pointer',
      mouseUpHandler: function(eventData, transform) {
        const target = transform.target;
        const canvas = target.canvas;
        canvas.remove(target);
        canvas.requestRenderAll();
        saveState();
        return true;
      },
      render: function(ctx, left, top, styleOverride, fabricObject) {
        ctx.save();
        ctx.translate(left, top);
        ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle));
        ctx.drawImage(deleteIcon, -12, -12, 24, 24);
        ctx.restore();
      },
      cornerSize: 24
    });

    fabric.Object.prototype.setControlsVisibility({
      mt: true,
      mb: true,
      ml: true,
      mr: true,
      bl: true,
      br: true,
      tl: true,
      tr: true,
      mtr: true,
      deleteControl: true
    });

    fabricRef.current.on('selection:created', function(e) {
      if (e.target && e.target.type === 'image') {
        e.target.setControlsVisibility({
          deleteControl: false
        });
      }
    });

    return () => {
      delete fabric.Object.prototype.controls.deleteControl;
    };
  }, [saveState]);

  useEffect(() => {
    if (!fabricRef.current) return;

    const handleLineDrawingClick = function(opt) {
      if (!isDrawingLine || activeButton !== 'line') return;
      
      const pointer = fabricRef.current.getPointer(opt.e);
      
      if (!lineStartPoint) {
        // First click - set start point
        setLineStartPoint({ x: pointer.x, y: pointer.y });
        
        // Create temporary visual feedback line
        const line = new fabric.Line([pointer.x, pointer.y, pointer.x, pointer.y], {
          stroke: currentColor,
          strokeWidth: 2,
          selectable: false,
          evented: false
        });
        
        fabricRef.current.add(line);
        tempLineRef.current = line;
      } else {
        // Second click - create the line
        if (tempLineRef.current) {
          fabricRef.current.remove(tempLineRef.current);
        }
        
        const line = new fabric.Line(
          [lineStartPoint.x, lineStartPoint.y, pointer.x, pointer.y], 
          {
            stroke: currentColor,
            strokeWidth: 2,
            selectable: true,
            hasControls: true,
            hasBorders: true,
            evented: true,
            perPixelTargetFind: false
          }
        );
        
        // Set custom controls for the line - only show endpoints
        line.setControlsVisibility({
          mt: false,
          mb: false,
          ml: false,
          mr: false,
          bl: false,
          br: false,
          tl: true,
          tr: true,
          mtr: false
        });
        
        fabricRef.current.add(line);
        fabricRef.current.setActiveObject(line);
        saveState();
        
        // Reset for the next line
        setLineStartPoint(null);
        tempLineRef.current = null;
      }
    };

    const handleLineDrawingMove = function(opt) {
      if (!isDrawingLine || !lineStartPoint || activeButton !== 'line' || !tempLineRef.current) return;
      
      const pointer = fabricRef.current.getPointer(opt.e);
      tempLineRef.current.set({
        x2: pointer.x,
        y2: pointer.y
      });
      fabricRef.current.renderAll();
    };

    // Simplified event handling for line drawing
    if (isDrawingLine && activeButton === 'line') {
      fabricRef.current.on('mouse:down', handleLineDrawingClick);
      fabricRef.current.on('mouse:move', handleLineDrawingMove);
      fabricRef.current.defaultCursor = 'crosshair';
    }

    return () => {
      if (fabricRef.current) {
        fabricRef.current.off('mouse:down', handleLineDrawingClick);
        fabricRef.current.off('mouse:move', handleLineDrawingMove);
        fabricRef.current.defaultCursor = 'default';
      }
    };
  }, [isDrawingLine, activeButton, lineStartPoint, currentColor, saveState]);

  useEffect(() => {
    if (!fabricRef.current) return;

    const handleObjectSelected = function(e) {
      if (e.target && e.target.type === 'line') {
        e.target.setControlsVisibility({
          mt: false,
          mb: false,
          ml: false,
          mr: false,
          bl: false,
          br: false,
          tl: true,
          tr: true,
          mtr: false
        });
        fabricRef.current.renderAll();
      }
    };

    fabricRef.current.on('selection:created', handleObjectSelected);
    fabricRef.current.on('selection:updated', handleObjectSelected);

    return () => {
      if (fabricRef.current) {
        fabricRef.current.off('selection:created', handleObjectSelected);
        fabricRef.current.off('selection:updated', handleObjectSelected);
      }
    };
  }, []);

  const handleColorChange = (color) => {
    setCurrentColor(color);
    if (fabricRef.current) {
      const activeObject = fabricRef.current.getActiveObject();
      if (activeObject) {
        if (activeObject.type === 'i-text') {
          activeObject.set('fill', color);
        } else {
          activeObject.set('stroke', color);
        }
        fabricRef.current.renderAll();
        saveState();
      }
      if (fabricRef.current.freeDrawingBrush) {
        fabricRef.current.freeDrawingBrush.color = color;
      }
    }
    setShowColorPicker(false);
  };

  const addShape = (type) => {
    if (type === 'line') {
      setIsDrawingLine(true);
      setActiveButton('line');
      fabricRef.current.isDrawingMode = false;
      fabricRef.current.selection = true;
      return;
    }
    
    setIsDrawingLine(false);
    
    let shape;
    switch (type) {
      case 'rectangle':
        shape = new fabric.Rect({
          left: 100,
          top: 100,
          width: 60,
          height: 60,
          stroke: currentColor,
          strokeWidth: 2,
          fill: 'transparent',
          selectable: true,
          hasControls: true,
          hasBorders: true,
          evented: true,
          perPixelTargetFind: false
        });
        break;
      case 'arrow':
        shape = new fabric.Path('M 0 0 L 120 0 L 115 -5 M 120 0 L 115 5', {
          left: 100,
          top: 100,
          stroke: currentColor,
          strokeWidth: 2,
          fill: 'transparent',
          selectable: true,
          hasControls: true,
          hasBorders: true,
          evented: true,
          perPixelTargetFind: false
        });
        break;
      case 'circle':
        shape = new fabric.Circle({
          radius: 20,
          left: 100,
          top: 100,
          stroke: currentColor,
          strokeWidth: 2,
          fill: 'transparent',
          selectable: true,
          hasControls: true,
          hasBorders: true,
          evented: true,
          perPixelTargetFind: false
        });
        break;
      case 'text':
        shape = new fabric.IText('Click to edit', {
          left: 100,
          top: 100,
          fontFamily: 'Arial',
          fill: currentColor,
          fontSize: 20,
          selectable: true,
          editable: true,
          hasControls: true,
          hasBorders: true
        });
        break;
      default:
        return;
    }

    fabricRef.current.discardActiveObject();
    
    fabricRef.current.add(shape);
    fabricRef.current.setActiveObject(shape);
    
    fabricRef.current.isDrawingMode = false;
    setActiveButton('move');
    
    fabricRef.current.renderAll();
    
    setTimeout(() => {
      saveState();
    }, 50);
  };

  const toggleDrawing = () => {
    setActiveButton('draw');
    setIsDrawingLine(false);
    if (fabricRef.current) {
      fabricRef.current.isDrawingMode = true;
      fabricRef.current.freeDrawingBrush = fabricRef.current.freeDrawingBrush || new fabric.PencilBrush(fabricRef.current);
      fabricRef.current.freeDrawingBrush.color = currentColor;
      const isMobile = window.innerWidth < 768;
      fabricRef.current.freeDrawingBrush.width = isMobile ? 3 : 2;
    }
  };

  const toggleEraser = () => {
    setActiveButton('eraser');
    setIsDrawingLine(false);
    if (fabricRef.current) {
      fabricRef.current.isDrawingMode = true;
      fabricRef.current.freeDrawingBrush = fabricRef.current.eraserBrush;
    }
  };

  const toggleMove = () => {
    setActiveButton('move');
    setIsDrawingLine(false);
    if (fabricRef.current) {
      fabricRef.current.isDrawingMode = false;
      fabricRef.current.selection = true;
    }
  };

  const handleSave = async () => {
    try {
      fabricRef.current.getObjects().forEach(obj => {
        obj.set({
          hasControls: false,
          hasBorders: false,
          selectable: false,
          active: false
        });
      });
      
      fabricRef.current.discardActiveObject();
      fabricRef.current.renderAll();

      const blob = await new Promise(resolve => {
        canvasRef.current.toBlob(resolve, 'image/jpeg', 0.8);
      });

      fabricRef.current.getObjects().forEach(obj => {
        if (obj.type !== 'image') {
          obj.set({
            hasControls: true,
            hasBorders: true,
            selectable: true
          });
        }
      });
      fabricRef.current.renderAll();

      onSave(blob);
    } catch (error) {
      console.error('Error saving image:', error);
      throw error;
    }
  };

  const handleZoomIn = () => {
    const newZoom = Math.min(fabricRef.current.getZoom() * 1.2, 5);
    const center = fabricRef.current.getCenter();
    fabricRef.current.zoomToPoint(new fabric.Point(center.left, center.top), newZoom);
    setZoom(newZoom);
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(fabricRef.current.getZoom() / 1.2, 0.5);
    const center = fabricRef.current.getCenter();
    fabricRef.current.zoomToPoint(new fabric.Point(center.left, center.top), newZoom);
    setZoom(newZoom);
  };

  const toggleColorPicker = () => {
    setShowColorPicker(!showColorPicker);
  };

  const tools = [
    { icon: MousePointer, name: 'move', action: toggleMove, tooltip: 'Move & Select' },
    { icon: Pencil, name: 'draw', action: toggleDrawing, tooltip: 'Draw' },
    { icon: Eraser, name: 'eraser', action: toggleEraser, tooltip: 'Eraser' },
    { 
      icon: Square, 
      name: 'shapes',
      action: () => setShowShapesMenu(true),
      tooltip: 'Add Shape',
      ref: shapesButtonRef,
      showChevron: true
    },
    { icon: Type, name: 'text', action: () => addShape('text'), tooltip: 'Add Text' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-stretch justify-center p-0 z-50">
      <div className="bg-white w-full h-full md:h-auto md:rounded-lg md:max-w-4xl flex flex-col">
        <div className="p-4 border-b flex justify-between items-center flex-shrink-0">
          <h3 className="text-lg font-medium">Edit Image</h3>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">
              {Math.round(zoom * 100)}%
            </span>
            <button
              onClick={handleZoomOut}
              className="p-2 rounded-lg text-slate-600 hover:bg-slate-100"
              title="Zoom Out"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <button
              onClick={handleZoomIn}
              className="p-2 rounded-lg text-slate-600 hover:bg-slate-100"
              title="Zoom In"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <div className="w-px h-6 bg-slate-200 mx-2" />
            <button onClick={onClose} className="text-slate-500 hover:text-slate-700">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-2 border-b flex gap-1 overflow-x-auto flex-shrink-0">
          {tools.map(({ icon: Icon, name, action, tooltip, ref, showChevron }) => (
            <button
              key={name}
              ref={ref}
              onClick={action}
              className={`p-2 rounded-lg flex-shrink-0 relative flex items-center gap-1 ${
                activeButton === name 
                  ? 'bg-blue-50 text-blue-600' 
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
              title={tooltip}
            >
              <Icon className="w-5 h-5" />
              {showChevron && <ChevronDown className="w-4 h-4" />}
            </button>
          ))}

          <button
            ref={colorButtonRef}
            onClick={toggleColorPicker}
            className={`p-2 rounded-lg flex-shrink-0 relative ${
              showColorPicker ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-100'
            }`}
            title="Change Color"
          >
            <Palette className="w-5 h-5" />
            <div 
              className="absolute bottom-1 right-1 w-2.5 h-2.5 rounded-full border border-white shadow-sm" 
              style={{ backgroundColor: currentColor }}
            />
          </button>
        </div>

        <div className="flex-1 flex items-center justify-center p-2 min-h-0">
          <canvas ref={canvasRef} className="max-w-full max-h-full rounded-lg shadow-inner" />
        </div>

        <div className="p-4 border-t flex justify-end gap-4 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
          >
            <Save className="w-4 h-4" /> Save Changes
          </button>
        </div>

        <ShapesMenu
          show={showShapesMenu}
          onClose={() => setShowShapesMenu(false)}
          onSelect={addShape}
          buttonRef={shapesButtonRef}
        />

        <ColorPicker
          show={showColorPicker}
          onClose={() => setShowColorPicker(false)}
          currentColor={currentColor}
          onColorChange={handleColorChange}
          buttonRef={colorButtonRef}
        />
      </div>
    </div>
  );
};

export default ImageEditor; 